import React from 'react';
import './Card.css';

/**
 * Card component with ArbiWin design system integration
 * Flexible card container for content display
 * Based on card styling from header_and_footer.html mockup
 *
 * @param {Object} props - Card properties
 * @param {React.ReactNode} props.children - Card content
 * @param {string} props.variant - Card variant: 'default', 'elevated', 'outlined', 'filled'
 * @param {string} props.size - Card size: 'small', 'medium', 'large'
 * @param {boolean} props.clickable - Make card clickable with hover effects
 * @param {Function} props.onClick - Click handler for clickable cards
 * @param {boolean} props.loading - Show loading state
 * @param {string} props.className - Additional CSS classes
 * @param {string} props.testId - Test ID for testing
 * @param {React.ElementType} props.as - HTML element or React component to render as
 * @returns {JSX.Element} Card component
 */
const Card = function Card({
  children,
  variant = 'default',
  size = 'medium',
  clickable = false,
  onClick,
  loading = false,
  className = '',
  testId,
  as: Component = 'div',
  ...rest
}) {

  const cardClasses = [
    'arbiwin-card',
    'arbiwin-card--' + variant,
    'arbiwin-card--' + size,
    clickable || onClick ? 'arbiwin-card--clickable' : '',
    loading ? 'arbiwin-card--loading' : '',
    className
  ].filter(Boolean).join(' ');

  const handleClick = function(event) {
    if (onClick && !loading) {
      onClick(event);
    }
  };

  const handleKeyDown = function(event) {
    if ((onClick || clickable) && !loading && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      handleClick(event);
    }
  };

  const cardProps = {
    className: cardClasses,
    'data-testid': testId || 'arbiwin-card',
    ...rest
  };

  if (onClick || clickable) {
    cardProps.onClick = handleClick;
    cardProps.onKeyDown = handleKeyDown;
    cardProps.tabIndex = loading ? -1 : 0;
    cardProps.role = 'button';
    cardProps['aria-disabled'] = loading;
  }

  return (
    <Component {...cardProps}>
      {loading && (
        <div className="arbiwin-card__loading-overlay" aria-hidden="true">
          <div className="arbiwin-card__loading-spinner">
            <svg viewBox="0 0 50 50" className="arbiwin-card__loading-icon">
              <circle
                cx="25"
                cy="25"
                r="20"
                fill="none"
                stroke="currentColor"
                strokeWidth="4"
                strokeDasharray="125.66"
                strokeDashoffset="125.66"
                strokeLinecap="round"
              />
            </svg>
          </div>
        </div>
      )}
      <div className="arbiwin-card__content">
        {children}
      </div>
    </Component>
  );
};

/**
 * CardHeader component for card titles and actions
 *
 * @param {Object} props - CardHeader properties
 * @param {string} props.title - Card title
 * @param {string} props.subtitle - Card subtitle
 * @param {React.ReactNode} props.actions - Header actions
 * @param {React.ReactNode} props.children - Custom header content
 * @param {string} props.className - Additional CSS classes
 * @returns {JSX.Element} CardHeader component
 */
const CardHeader = function CardHeader({
  title,
  subtitle,
  actions,
  children,
  className = ''
}) {

  const headerClasses = [
    'arbiwin-card-header',
    className
  ].filter(Boolean).join(' ');

  if (children) {
    return (
      <div className={headerClasses}>
        {children}
      </div>
    );
  }

  return (
    <div className={headerClasses}>
      <div className="arbiwin-card-header__content">
        {title && (
          <h3 className="arbiwin-card-header__title">
            {title}
          </h3>
        )}
        {subtitle && (
          <p className="arbiwin-card-header__subtitle">
            {subtitle}
          </p>
        )}
      </div>
      {actions && (
        <div className="arbiwin-card-header__actions">
          {actions}
        </div>
      )}
    </div>
  );
};

/**
 * CardBody component for main card content
 *
 * @param {Object} props - CardBody properties
 * @param {React.ReactNode} props.children - Body content
 * @param {string} props.className - Additional CSS classes
 * @returns {JSX.Element} CardBody component
 */
const CardBody = function CardBody({
  children,
  className = ''
}) {
  return (
    <div className={'arbiwin-card-body ' + className}>
      {children}
    </div>
  );
};

/**
 * CardFooter component for card actions and additional info
 *
 * @param {Object} props - CardFooter properties
 * @param {React.ReactNode} props.children - Footer content
 * @param {React.ReactNode} props.actions - Footer actions
 * @param {string} props.align - Footer alignment: 'left', 'center', 'right', 'space-between'
 * @param {string} props.className - Additional CSS classes
 * @returns {JSX.Element} CardFooter component
 */
const CardFooter = function CardFooter({
  children,
  actions,
  align = 'right',
  className = ''
}) {

  const footerClasses = [
    'arbiwin-card-footer',
    'arbiwin-card-footer--' + align,
    className
  ].filter(Boolean).join(' ');

  return (
    <div className={footerClasses}>
      {children}
      {actions && (
        <div className="arbiwin-card-footer__actions">
          {actions}
        </div>
      )}
    </div>
  );
};

/**
 * SurebetCard component - Pre-configured card for surebet data
 * Specialized card for displaying arbitrage opportunities
 *
 * @param {Object} props - SurebetCard properties
 * @param {Object} props.surebet - Surebet data object
 * @param {Function} props.onCalculatorOpen - Calculator open handler
 * @param {boolean} props.loading - Loading state
 * @param {string} props.className - Additional CSS classes
 * @returns {JSX.Element} SurebetCard component
 */
const SurebetCard = function SurebetCard({
  surebet,
  onCalculatorOpen,
  loading = false,
  className = ''
}) {

  if (!surebet) {
    return (
      <Card variant="outlined" loading className={'arbiwin-surebet-card ' + className}>
        <CardBody>
          <p>No surebet data available</p>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card
      variant="elevated"
      clickable
      loading={loading}
      className={'arbiwin-surebet-card ' + className}
    >
      <CardHeader
        title={surebet.event?.teamOne + ' vs ' + surebet.event?.teamTwo}
        subtitle={surebet.event?.championship + ' • ' + surebet.event?.date}
      />
      <CardBody>
        <div className="arbiwin-surebet-card__margins">
          {Object.entries(surebet.margins || {}).map(function([marginKey, marginData]) {
            return (
              <div key={marginKey} className="arbiwin-surebet-card__margin">
                <span className="arbiwin-surebet-card__margin-type">
                  {marginKey}
                </span>
                <span className="arbiwin-surebet-card__margin-value">
                  {(marginData.value * 100).toFixed(2)}%
                </span>
              </div>
            );
          })}
        </div>
      </CardBody>
      <CardFooter
        actions={
          <button
            className="arbiwin-button arbiwin-button--primary arbiwin-button--small"
            onClick={function(e) {
              e.stopPropagation();
              if (onCalculatorOpen) {
                onCalculatorOpen(surebet);
              }
            }}
          >
            Калкулатор
          </button>
        }
      />
    </Card>
  );
};

/**
 * FeatureCard component - Card for displaying features
 *
 * @param {Object} props - FeatureCard properties
 * @param {React.ReactNode} props.icon - Feature icon
 * @param {string} props.title - Feature title
 * @param {string} props.description - Feature description
 * @param {string} props.className - Additional CSS classes
 * @returns {JSX.Element} FeatureCard component
 */
const FeatureCard = function FeatureCard({
  icon,
  title,
  description,
  className = ''
}) {
  return (
    <Card
      variant="outlined"
      className={'arbiwin-feature-card ' + className}
    >
      <CardBody>
        {icon && (
          <div className="arbiwin-feature-card__icon">
            {icon}
          </div>
        )}
        {title && (
          <h3 className="arbiwin-feature-card__title">
            {title}
          </h3>
        )}
        {description && (
          <p className="arbiwin-feature-card__description">
            {description}
          </p>
        )}
      </CardBody>
    </Card>
  );
};

/**
 * StatsCard component - Card for displaying statistics
 *
 * @param {Object} props - StatsCard properties
 * @param {string} props.label - Stat label
 * @param {string|number} props.value - Stat value
 * @param {string} props.change - Change indicator
 * @param {string} props.trend - Trend: 'up', 'down', 'neutral'
 * @param {React.ReactNode} props.icon - Stat icon
 * @param {string} props.className - Additional CSS classes
 * @returns {JSX.Element} StatsCard component
 */
const StatsCard = function StatsCard({
  label,
  value,
  change,
  trend = 'neutral',
  icon,
  className = ''
}) {
  return (
    <Card
      variant="filled"
      size="small"
      className={'arbiwin-stats-card arbiwin-stats-card--' + trend + ' ' + className}
    >
      <CardBody>
        <div className="arbiwin-stats-card__content">
          {icon && (
            <div className="arbiwin-stats-card__icon">
              {icon}
            </div>
          )}
          <div className="arbiwin-stats-card__data">
            <div className="arbiwin-stats-card__value">
              {value}
            </div>
            <div className="arbiwin-stats-card__label">
              {label}
            </div>
            {change && (
              <div className="arbiwin-stats-card__change">
                {change}
              </div>
            )}
          </div>
        </div>
      </CardBody>
    </Card>
  );
};

Card.displayName = 'Card';
CardHeader.displayName = 'CardHeader';
CardBody.displayName = 'CardBody';
CardFooter.displayName = 'CardFooter';
SurebetCard.displayName = 'SurebetCard';
FeatureCard.displayName = 'FeatureCard';
StatsCard.displayName = 'StatsCard';

export default Card;
export {
  CardHeader,
  CardBody,
  CardFooter,
  SurebetCard,
  FeatureCard,
  StatsCard
};
